import { VoyageDialogPopupManager } from "../../local_modules/voyage-dialog-popup/src/voyage-dialog-popup-manager";
import { VesselPlanningModel } from "../models/vessel-planning-model";
import { VesselPositionModel } from "../models/vessel-position-model";
import { CoordinatesUtils } from "./coordinates-utils";
import { CreateDateLineAndGreatCircleFeatures, getLegTypeFromDist } from "./map-utils";
import { getDistance as gcDistance } from 'ol/sphere';
import { VoyageDialogPopupBodyWarning } from "../../local_modules/voyage-dialog-popup/src/voyage-dialog-popup-body-warning";
import { TerminalsModel } from "../models/terminals-model";
import { SvgIcons } from "../../local_modules/voyage-svg-icons/src/voyage-svg-icons";
import { GetFormattedLocalDate } from "./helpers";
import { WaypointTimestamps, PilotPointTimestamps, TimeseriesData, TimestampData, ChartNames } from "../../local_modules/voy-timeseries-chart/src/echartUtils";
import { VoyageSchedulesModel } from "../models/voyage-schedules-model";
import { ScheduleEntry } from "../models/types/voyage-schedules-model.types";
import { VoyagePlan } from "../models/vessel-planning-model";
import { VoyageRoute, RouteGeometry, VoyageRoutePointFeature, VoyageRouteLineFeature, RoutingJobOptions } from "../models/voyage-routing-model";
import { VoyageRoutingModel } from "../models/voyage-routing-model";
import { Feature } from 'ol';
import { Geometry, LineString } from 'ol/geom';
import { lineString as turfLine, Feature as TurfFeature, LineString as turfLineString } from '@turf/helpers';
import lineIntersect from '@turf/line-intersect';

export enum WPTypeDescription {
    RegularWP = "WP",
    ArrivalPilot = "arrival pilot point",
    DeparturePilot = "departure pilot point",
    ArrivalBerth = "arrival berth"
}

// Convert a uiType string to an index (returns -1 for no or unknown uiType strings)
export function uiTypeToIndex(uiType?: string): number
{
    let index = -1;
    if (uiType)
    {
        const regexMatch = /UserRoute([0-9]+)/.exec(uiType);
        if (regexMatch)
        {
            index = Number(regexMatch[1]);
        }
    }
    return index;
}

export async function markPassedWaypoints(route: VoyageRoute, ignoreIsPassedUpdate?: boolean, nextWPIndex?: number)
{
    if (nextWPIndex === undefined)
    {
        nextWPIndex = await getNextWaypointIndex(route.route_geometry, true);
    }

    const timeDelta = 1000*60*30; // Use existing isPassed if newer than 30 minutes to allow the user to correct the state
    if (ignoreIsPassedUpdate || !route.isPassedUpdate || new Date(route.isPassedUpdate).getTime() < Date.now() - timeDelta)
    {
        // Mark passed waypoints
        for (let i = 0; i < nextWPIndex; i++)
        {
            if (route.route_geometry[i].geometry.type === "Point")
            {
                route.route_geometry[i].properties.isPassed = true;
            }
        }
    }
}

export async function markPassedAndAddCurrentPosition(route: VoyageRoute, ignoreIsPassedUpdate?: boolean, ignoreDeviation?: boolean)
{
    const nextWPIndex = await getNextWaypointIndex(route.route_geometry, ignoreDeviation);
    markPassedWaypoints(route, ignoreIsPassedUpdate, nextWPIndex);
    insertCurrentPositionWP(route.route_geometry, nextWPIndex);
}

export async function getNextWaypointIndex(geometry: RouteGeometry, ignoreDeviation?: boolean): Promise<number>
{
    let nextWPIndex;
    const position = VesselPositionModel!.position;
    if (!position || position.latitude === null || position.longitude === null)
    {
        console.warn("Missing position info");
        throw new Error("Missing current position information");
    }

    // Note: Allowing 50 nm deviation from route
    const allowedDeviation = ignoreDeviation ? -1 : 92600;
    const result = await VoyageRoutingModel!.getNextWaypointIndex(allowedDeviation, [position.longitude, position.latitude], geometry);
    if (result.result !== "failure")
    {
        nextWPIndex = result.nextWPIndex;
    }
    else
    {
        console.warn("Failed to calculate next waypoint! Result: ", result);
        throw new Error("Deviation from planned route too big")
    }

    return nextWPIndex;
}

export function insertCurrentPositionWP(geometry: RouteGeometry, index?: number)
{
    const position = VesselPositionModel!.position;
    if (!position)
    {
        // No current position, bail out
        return;
    }

    let legType: "GreatCircle"|"Rhumbline"|undefined;
    let speed_maximum: number|undefined;
    let speed_minimum: number|undefined;

    if (!index)
    {
        index = geometry.length;
    }
    else
    {
        // Get leg type from leg that we are partitioning
        legType = geometry[index-1].properties.leg_type;

        // Carry over speed limits, if any
        speed_maximum = geometry[index-2].properties.speed_maximum;
        speed_minimum = geometry[index-2].properties.speed_minimum;

    }

    const coords: [number,number] = [position.longitude!, position.latitude!];

    // Check if the current position is already present
    for (const feature of geometry)
    {
        if (feature.geometry.type === "Point" &&
        feature.geometry.coordinates[0] === coords[0] && feature.geometry.coordinates[1] == coords[1])
        {
            // Clear isPassed if set (since we need to include this WP in the calculation)
            delete feature.properties.isPassed;
            return;
        }
    }

    const newFeatures: RouteGeometry = [];
    const time = new Date().toISOString();
    const newPoint: VoyageRoutePointFeature = {
        type: "Feature",
        geometry: { type: "Point", coordinates: coords },
        properties: {
            type: "Navigational",
            vesselPositionAt: time,
            timestamp: time
        }
    };
    if (speed_maximum !== undefined)
    {
        newPoint.properties.speed_maximum = speed_maximum;
    }
    if (speed_minimum !== undefined)
    {
        newPoint.properties.speed_minimum = speed_minimum;
    }
    newFeatures.push(newPoint);

    if (index < geometry.length)
    {
        newFeatures.push({
            type: "Feature",
            geometry: {
                type: "LineString",
                coordinates: [coords, <[number,number]>geometry[index].geometry.coordinates]
            },
            properties: {
                leg_type: legType || getLegTypeFromDist(coords, <[number,number]>geometry[index].geometry.coordinates)
            }
        });
    }
    
    // Need to modify the leg before and insert a new point and new leg
    if (index > 0)
    {
        for (let i = index-1; i >= 0; i--)
        {
            if (geometry[i].geometry.type === "LineString")
            {
                // Properties we keep:
                // - leg_type
                // - distance (recalculated based on new end point)
                // - propulsion_power/propulsion_power_lower/propulsion_power_upper (since it is constant along the leg)
                // - main_engine_rpm/main_engine_rpm_lower/main_engine_rpm_upper (since it is constant along the leg)
                // - speed_over_ground - but taken from the first informational waypoint (if any), since the value is an average otherwise
                // - speed_through_water - but taken from the first informational waypoint (if any), since the value is an average otherwise
                // The rest is discarded, since the leg is modified
                const leg = <VoyageRouteLineFeature>geometry[i];
                leg.geometry.coordinates[1] = coords;
                const legType = leg.properties.leg_type;
                const distance = legType === "Rhumbline" ? CoordinatesUtils.calcRhumblineDist(leg.geometry.coordinates[0], leg.geometry.coordinates[1])/1000 :
                                                           gcDistance(leg.geometry.coordinates[0], leg.geometry.coordinates[1])/1000;

                leg.properties = {
                    leg_type: legType,
                    distance: distance,
                    propulsion_power: leg.properties.propulsion_power,
                    propulsion_power_lower: leg.properties.propulsion_power_lower,
                    propulsion_power_upper: leg.properties.propulsion_power_upper,
                    main_engine_rpm: leg.properties.main_engine_rpm,
                    main_engine_rpm_lower: leg.properties.main_engine_rpm_lower,
                    main_engine_rpm_upper: leg.properties.main_engine_rpm_upper,
                    speed_over_ground: leg.properties.informationalWaypoints ? leg.properties.informationalWaypoints[0].properties.speed_over_ground : leg.properties.speed_over_ground,
                    speed_through_water: leg.properties.informationalWaypoints ? leg.properties.informationalWaypoints[0].properties.speed_through_water : leg.properties.speed_through_water,
                };
                break;
            }
        }
    }
    geometry.splice(index, 0, ...newFeatures);
}

export function SaveHydraLog(hydraRequestId: string) {
    const routingServiceEnv = (<any>window)["appenv"].services.routeoptimiserapi;
    const name = `${hydraRequestId}.zip`;

    const exportElement = document.createElement('a');
    exportElement.download = name;
    exportElement.href = `${routingServiceEnv.baseurl}/v1/hydra-logs/${hydraRequestId}.zip`
    exportElement.click();    
}

// Recalculate routes for the given plan and save the result
// If noChanges are set, the recalculation is solely to get an updated calculation without any actual changes to the route or plan,
// otherwise it is assumed that something (non-passage plan related) has been changed in the plan that invalidates the current calculation(s)
// Note that it is assumed that the passage plan is unchanged in this function; If that is not the case, set plan.status before calling this function
export async function recalcRoutes(plan: VoyagePlan, waitForCalcJobs?: boolean, noChanges?: boolean): Promise<void>
{
    const wasPlanApproved = plan.status === "approved";
    const selectRouteIndex = plan.selectedRouteIndex;

    if (!noChanges)
    {
        // Save plan before calculating so changes are not lost, BUT:
        // 1) Set status to planning until the new route(s) is/are actually calculated
        // 2) Remove route_infos for the routes to signify they haven't been calculated yet
        // 3) Reset selectedRouteIndex until the calculation finishes (since you cannot have a non-calculated route selected)
        plan.status = "planning";
        plan.selectedRouteIndex = -1;
        for (let i = 0; i < plan.routes.length; i++)
        {
            delete plan.routes[i].route_info;
        }
        await VesselPlanningModel!.setPlan(plan.scheduleId, plan);
    }

    const executingPlan = await VesselPlanningModel!.getExecutingPlan();
    const calcJobs: Promise<VoyageRoute>[] = [];

    if (executingPlan && executingPlan.scheduleId === plan.scheduleId)
    {
        // Executing plan - update route(s) from current position
        for (let i = 0; i < plan.routes.length; i++)
        {
            const route = plan.routes[i];
            try
            {
                await markPassedAndAddCurrentPosition(route);
            }
            catch (err: any)
            {
                if (i === plan.selectedRouteIndex)
                {
                    VoyageDialogPopupManager.createDialog({
                        headerText: "Re-calculation error",
                        bodyText: 'Failed to add current position to route. Please check route by using "Edit route"',
                        isError: true,
                        errorDetails: err.message
                    });    
                }
                continue;
            }

            const options: RoutingJobOptions = {
                draftFore: plan.foreDraft,
                draftAft: plan.aftDraft,
                gmt: plan.gmt !== null ? plan.gmt : undefined,
                etd: Date.now(),
                eta: plan.eta,
                numberOfReefers: plan.numberOfReefers,
                routeType: "WeatherOptimized",
                uiRouteIndex: getUiRouteIndex(route),
                planScheduleId: plan.scheduleId,
                updatePlan: {
                    routeIndex: i,
                    autoApprove: wasPlanApproved,
                    selectRoute: i === selectRouteIndex
                },
                passagePlan: route.passagePlan
            }

            if (route.route_info) {
                options.whaleZonesApplied = route.route_info!.whaleZonesApplied;
            }

            calcJobs.push(VoyageRoutingModel!.postRoutingJobUsingRoute(route, options));
        }
    }
    else
    {
        for (let i = 0; i < plan.routes.length; i++)
        {
            const route = plan.routes[i];
            const options: RoutingJobOptions = {
                draftFore: plan.foreDraft,
                draftAft: plan.aftDraft,
                gmt: plan.gmt !== null ? plan.gmt : undefined,
                etd: plan.etd,
                eta: plan.eta,
                numberOfReefers: plan.numberOfReefers,
                routeType: "WeatherOptimized",
                uiRouteIndex: getUiRouteIndex(plan.routes[i]),
                planScheduleId: plan.scheduleId,
                updatePlan: {
                    routeIndex: i,
                    autoApprove: wasPlanApproved,
                    selectRoute: i === selectRouteIndex
                },
                passagePlan: route.passagePlan
            }

            if (route.route_info) {
                options.whaleZonesApplied = route.route_info!.whaleZonesApplied;
            }

            calcJobs.push(VoyageRoutingModel!.postRoutingJobUsingRoute(route, options));
        }
    }

    if (waitForCalcJobs)
    {
        await Promise.all(calcJobs);
    }
}

export function getUiRouteIndex(route: VoyageRoute): number|undefined
{
    if (route.uiType)
    {
        const regexMatch = /UserRoute([0-9]+)/.exec(route.uiType);
        if (regexMatch)
        {
            return Number(regexMatch[1]);
        }
    }
}

export function getPilotPointIndeces(route: VoyageRoute): number[]
{
    const ppIndeces: number[] = [];
    const geometry = route.route_geometry;

    for (let i = 0; i < geometry.length; i++)
    {
        if (!geometry[i].geometry || geometry[i].geometry.type !== "Point") continue;

        if (geometry[i].properties.pilotPoint)
        {
            ppIndeces.push(i);
        }
    }
    return ppIndeces;
}

// Warn user if requested ETA on any WP on route deviate from calculated ETA by 0.11% (minimum 10 minutes)
// Vessels might need to go slower than 10% MCR from:
//  - departure berth to departure pilot
//  - arrival pilot to arrival berth
// Hydra does not account for this due to a fixed minimum 10% MCR in their calculations.
// Calculated ETA between the above mentioned WPs might therefore not be correct. Therefore,
// if vessel is early between the above mentioned, then don't show warning.

/**
 * @name verifyRequestedETA
 * @description Warn user if requested ETA on any WP on route deviate from calculated ETA by 0.11% (minimum 10 minutes)
    * Vessels might need to go slower than 10% MCR from:
    *  - departure berth to departure pilot
    *  - arrival pilot to arrival berth
    * Hydra does not account for this due to a fixed minimum 10% MCR in their calculations.
    * Calculated ETA between the above mentioned WPs might therefore not be correct. Therefore,
    * if vessel is early between the above mentioned, then don't show warning.
    * @param {VoyageRoute} route route
    * @param {string} planScheduleId ID of current schedule
    * 
    * @returns {Promise<void>} void
*/
export async function verifyRequestedETA(route: VoyageRoute, planScheduleId: string | undefined): Promise<void>
{
    if (!route.route_geometry.length) return;

    let WPNumber: number = 0;
    let arrivalPilotIndex = -1;
    let departurePilotIndex = -1;
    let currentSegmentStart = 0;
    

    const pilotPoints = getPilotPointIndeces(route);
    if (pilotPoints.length > 0)
    {
        departurePilotIndex = pilotPoints[0];
        arrivalPilotIndex = pilotPoints[pilotPoints.length-1];
    }

    // remainingVoyageStartTS is used to calculate remaining voyage duration and allowed deviation of 0.11%
    let remainingVoyageStartTS = departurePilotIndex > -1 ? route.route_geometry[pilotPoints[0]].properties.timestamp : route.route_geometry[0].properties.timestamp;

    for (let i = 0; i < route.route_geometry.length; i++) 
    {
        const feature = route.route_geometry[i];

        if (feature.geometry.type !== "Point")
        {
            continue;
        }

        let WPType: string;

        // navigational WPs are not numbered in the route editor
        if (route.route_geometry[i].properties.type === "Hard")
        {
            WPType = "hard";
            WPNumber++;
        }
        else
        {
            WPType = "informational";
        }

        const isLastWP = i === route.route_geometry.length-1;

        if (feature.properties.isPassed)
        {
            remainingVoyageStartTS = feature.properties.timestamp;
            continue; // skip verification on passed part of voyage
        }

        if (feature.properties.vesselPositionAt) {
            remainingVoyageStartTS = feature.properties.vesselPositionAt;
            continue; // skip verification on passed part of voyage
        }

        if (!feature.properties.eta && !isLastWP) {
            continue;
        }

        const requestedETAString = isLastWP ? route.route_info!.eta : route.route_geometry[i].properties.eta!;
        const calculatedETAString = route.route_geometry[i].properties.timestamp!;
        const calculatedETANumber = new Date(calculatedETAString!).getTime()/(60*1000);
        const requestedETANumber = new Date(requestedETAString!).getTime()/(60*1000);

        const WP = <VoyageRoutePointFeature>feature;

        // Note on pilot point special cases:
        // If there is only one pilot point defined, it is assumed to be both departure and arrival pilot point (ie. there is nothing in between)
        // If there are no pilot points defined, everything is treated as being in between the departure and arrival pilot points
        const pastDeparturePilot: boolean = i > departurePilotIndex;
        const pastArrivalPilot: boolean = arrivalPilotIndex !== -1 && i > arrivalPilotIndex;
        
        const earlyArrival = calculatedETANumber - requestedETANumber < 0;
        // Skip further checks if one of these conditions are true:
        // - We have passed the arrival pilot point and are early (we never warn about early arrival at berth, only up to the arrival pilot)
        // - We have not passed the departure pilot point yet (we never warn about anything until we pass the departure pilot)
        if ((earlyArrival && pastArrivalPilot) || (pilotPoints.length > 1 && earlyArrival && !pastDeparturePilot))
        {
            currentSegmentStart = i;
            continue;
        }
        
        // We consider the ETA to be missed if the calculated arrival is 10 minutes or more off
        const remainingVoyageStartTime = new Date(remainingVoyageStartTS).getTime()/(60*1000);
        let allowedDeviation = 10;
        const remainingVoyageDuration = calculatedETANumber - remainingVoyageStartTime;
        // find 0.11% of remaining voyage which is allowed deviation
        const allowedRemainingVoyageDeviation = (remainingVoyageDuration * 0.0011);
        // allowed deviation should not go below 10 min
        if (allowedRemainingVoyageDeviation > 10) {
            allowedDeviation = allowedRemainingVoyageDeviation;
        }

        if (Math.abs(requestedETANumber - calculatedETANumber) >= allowedDeviation)
        {
            let WPDescription: string|null = null; // default is null for ordinary WP, no description needed
            let MCRLimitHit = false;

            if (earlyArrival)
            {
                MCRLimitHit = _earlyDueToMCRLimit(route.route_info!.hydraErrors, route.route_geometry.slice(currentSegmentStart, i));
            }
            if (WP.properties.pilotPoint)
            {
                if (i === departurePilotIndex)
                {
                    WPDescription = WPTypeDescription.DeparturePilot;
                }
                if (i === arrivalPilotIndex)
                {
                    WPDescription = WPTypeDescription.ArrivalPilot;
                }
            }
            if (isLastWP)
            {
                WPDescription = WPTypeDescription.ArrivalBerth;
            }

            const coords = WP.geometry.coordinates;
            let currentPlan = planScheduleId ? await VesselPlanningModel!.getCurrentPlan(planScheduleId) : null;
            _showETAMissedWarning(coords, calculatedETAString, requestedETAString, WPDescription, currentPlan, WPType, WPNumber, MCRLimitHit);
            return;
        }
    }
}

function _earlyDueToMCRLimit(hydraErrors: any[]|undefined, routeSegment: RouteGeometry): boolean
{
    // Try to determine if we are early due to the 10% MCR minimum power limit
    if (hydraErrors) 
    {
        let hydraErrorMatch = false;

        try
        {
            // Example hydra error string: "Cannot meet ETA: 1.0 hours early. Applied min power 6184.0 kW on segments where speed restrictions allows it."
            const earlyRegEx = /.*early.*Applied min power.*/
            for (const hydraError of hydraErrors)
            {
                if (earlyRegEx.test(hydraError))
                {
                    hydraErrorMatch = true;
                    break;
                }
            }
        }
        catch (e)
        {
            // Hydra errors may have changed format, default to returning false while logging a warning
            console.warn("Failed to parse hydra errors", e);
        }
        
        if (hydraErrorMatch)
        {
            // Hydra returns the same error when it is actually limited by min speed and not the 10% MCR limit; Check if this is the case
            for (let i = 0; i < routeSegment.length - 1; i += 2)
            {
                const minSpeed = routeSegment[i].properties.min_speed;
                const sog = routeSegment[i].properties.speed_over_ground;
                if (!minSpeed || sog-minSpeed > 0.5)
                {
                    // No min speed specified, or the speed is significantly higher => MCR limit must be the cause
                    return true;
                }
            }
        }
    }

    return false;
}

async function _showETAMissedWarning(coords: [number, number], calculatedETAString: string, requestedETAString: string, WPDescription: string|null,
                                     currentPlan: VoyagePlan|null, WPType: string, WPNumber: number, MCRLimitHit: boolean): Promise<void>
{
    if (calculatedETAString && requestedETAString)
    {
        let calculatedETALocalTime = calculatedETAString;
        let requestedETALocalTime = requestedETAString;
        let localTime = false;
        
        // convert to local time if WP is arrival berth or arrival pilot
        if (currentPlan)
        {
            if (WPDescription === WPTypeDescription.ArrivalBerth || WPDescription === WPTypeDescription.ArrivalPilot)
            {
                calculatedETALocalTime = await TerminalsModel!.getTerminalLocaltime(currentPlan!.arrivalTerminalCode, calculatedETAString);
                requestedETALocalTime = await TerminalsModel!.getTerminalLocaltime(currentPlan!.arrivalTerminalCode, requestedETAString);
                localTime = true;
            }
            if (WPDescription === WPTypeDescription.DeparturePilot)
            {
                calculatedETALocalTime = await TerminalsModel!.getTerminalLocaltime(currentPlan!.departureTerminalCode, calculatedETAString);
                requestedETALocalTime = await TerminalsModel!.getTerminalLocaltime(currentPlan!.departureTerminalCode, requestedETAString);
                localTime = true;
            }
        }
        const position = CoordinatesUtils.decimalToDegreesAndMinutes(coords)

        const warningElement = new VoyageDialogPopupBodyWarning();
        warningElement.icon = SvgIcons.lightExclamationCircle;
        warningElement.warningSummary = `Requested ETA of ${GetFormattedLocalDate(requestedETALocalTime)} ${localTime ? "(local time)" : "(UTC)"} at waypoint ${WPType === "hard" ? "#"+WPNumber : ""} at ${position.lat}, ${position.lon} ${WPDescription ? WPDescription : ""} cannot be met`;

        if (MCRLimitHit) {
            warningElement.warningSummary += ` due to required speed is less than 10% MCR`;
        }

        warningElement.warningDescription = `Actual arrival calculated to ${GetFormattedLocalDate(calculatedETALocalTime)} ${localTime ? "(local time)" : "(UTC)"}`;
        warningElement.descriptionTextAlign = "center";

        VoyageDialogPopupManager.createDialog({
            headerText: "Requested ETA cannot be met" + (MCRLimitHit ? " due to required speed is less than 10% MCR" : ""),
            bodyContent: warningElement,
        });
    }
    else
    {
        console.warn("ETA info is missing; cannot verify requested ETA - ", "Requested ETA: ", requestedETAString, "Calculated ETA: ", calculatedETAString)
    }
}



export function getRouteDistance(route: VoyageRoute, startIdx: number, endIdx: number): number {
    let totalDist = 0;
    for (let i = startIdx; i <= endIdx; i++) {
        if (route.route_geometry[i].geometry.type === "Point") {
            continue;
        }
        totalDist += route.route_geometry[i].properties.distance;
    }
    return totalDist;
}

export function getRoutePropertyAverages(route: VoyageRoute, startIdx: number, endIdx: number)
    {
        const geometry = route.route_geometry;

        let totalDistance = 0;
        for (let i = startIdx; i < endIdx; i++)
        {
            if (geometry[i].geometry.type === "Point") continue;

            totalDistance += geometry[i].properties.distance;
        }

        let avgSTW = 0;
        let avgRPM = 0;
        let avgSpeed = 0;
        
        for (let i = startIdx; i < endIdx; i++)
        {
            if (geometry[i].geometry.type === "Point") continue;
            
            const legDistance = geometry[i].properties.distance;
            const legAvgRatio = legDistance / totalDistance;
            
            avgRPM += geometry[i].properties.main_engine_rpm * legAvgRatio;
            avgSTW += geometry[i].properties.speed_through_water * legAvgRatio;
            avgSpeed += geometry[i].properties.speed_over_ground * legAvgRatio;
        }
        
        return {
            rpm: avgRPM,
            stw: avgSTW,
            speed: avgSpeed
        }
    }

export function getPilotPointsTimestamps(plan: VoyagePlan) {
    let departure = -1;
    let arrival = -1;

    if (plan.selectedRouteIndex === -1) {
        return { departure, arrival }
    }

    const route = plan.routes[plan.selectedRouteIndex];

    if (!route.route_info) {
        return { departure, arrival }
    }

    const ppIndeces = getPilotPointIndeces(route);
    departure = new Date(route.route_geometry[ppIndeces[0]].properties.timestamp).getTime();
    arrival = new Date(route.route_geometry[ppIndeces[ppIndeces.length-1]].properties.timestamp).getTime();


    return {
        departure,
        arrival
    };
}

export function getHardWaypointsInfoFromRoutes(routes: VoyageRoute[]): WaypointTimestamps[][] {
    if (!routes.length) return [];

    const hardWaypoints = [];
    for (const route of routes) {
        // if route_info is not present, it means the route has not been calculated
        // and thus there will be no timestamps
        if (!route.route_info) {
            continue;
        }
        const ppIndeces = getPilotPointIndeces(route);
        let startIdx = ppIndeces.length ? ppIndeces[0]+2 : 0;
        let endIdx = ppIndeces.length > 1 ? ppIndeces[ppIndeces.length-1]-2 : route.route_geometry.length-1;
        
        let wpNumber = 0
        const waypoints = [];
        for (let i = 0; i < endIdx+1; i++) {
            const feature = route.route_geometry[i];
            
            // skip linestings and informational waypoints
            if (feature.geometry.type === "LineString" || feature.properties.type !== "Hard") continue;

            wpNumber++;

            if (i < startIdx) continue;
            if (i > endIdx) break;

            waypoints.push(
                {
                    wpNumber,
                    timestamp: new Date(feature.properties.timestamp).getTime()
                }
            )
        }
        hardWaypoints.push(waypoints);
    }
    return hardWaypoints;
}

// Need to aggregate the times for all the data series, since echarts tooltips don't really work unless the data is uniform
    // - i.e. they all need to have the same x-axis entries
    // Using a set to avoid duplicating times
export function extractAllTimestamps(pilotPoints: PilotPointTimestamps, data: {[index in ChartNames]: TimeseriesData[]}, waypoints: WaypointTimestamps[][]) {
    let timesMap = new Map<number, TimestampData>();
    
    // pilot points timestamps
    if (pilotPoints.arrival > 0) {
        timesMap.set(pilotPoints.arrival, { timestamp: pilotPoints.arrival, isWp: false });
    }
    if (pilotPoints.departure > 0) {
        timesMap.set(pilotPoints.departure, { timestamp: pilotPoints.departure, isWp: false });
    }

    // performance and weather data
    for (const key in data) {
        for (const route of data[<ChartNames>key]) {
            for (const ts of route.timeseries.timestamps) {
                timesMap.set(ts, { timestamp: ts, isWp: false })
            }
        }
    }

    // waypoints timestamps
    for (const [idx, route]of waypoints.entries()) {
        for (const wp of route) {
            if (timesMap.has(wp.timestamp)) {
                timesMap.get(wp.timestamp)!.isWp = true;
                timesMap.get(wp.timestamp)!.wpNumber = wp.wpNumber;
                timesMap.get(wp.timestamp)!.routeIdx = idx;
                continue;
            }
            timesMap.set(wp.timestamp, { timestamp: wp.timestamp, isWp: true, routeIdx: idx, wpNumber: wp.wpNumber });
        }
    }

    const aggregatedTimes = Array.from(timesMap.values());
    aggregatedTimes.sort((a, b) => a.timestamp - b.timestamp);

    return aggregatedTimes;
}

/**
 * @name handleWeatherRouting
 * @description Show popup warning if weather is Beaufort 8 or route is crossing
 * weather routing lines. 
 */
export async function handleWeatherRouting(inputPlan: VoyagePlan) {
    let plan = inputPlan;
    const scheduleId = plan.scheduleId;

    // Don't handle warning on both executing and planning plan.
    // Only use executing plan if there is one.
    const executingPlan = await VesselPlanningModel!.getExecutingPlan();
    if (executingPlan && executingPlan.scheduleId === scheduleId) {
        plan = executingPlan;
    }

    const routePassagePlanProperties = plan.routes[plan.selectedRouteIndex].passagePlan;
    
    // Add properties to plan, if they are not present
    if (!routePassagePlanProperties!.weatherRouting || !routePassagePlanProperties!.weatherRouting.hasOwnProperty("required")) {
		routePassagePlanProperties!.weatherRouting = {
            required: false,            
		    acknowledged: false,     
		    pilotPointAcknowledged: false 
        }
	}

    const scheduleEntries = await VoyageSchedulesModel!.getScheduleFor((<any>window)["appenv"].vesselIMONumber);
    if (!scheduleEntries) return;

    const shouldCheckWarning = isActiveOrNextScheduleEntry(scheduleId, scheduleEntries);
    if (!shouldCheckWarning) return;

    const route = plan.routes[plan.selectedRouteIndex];
    if (!route.passagePlan) return;

    routePassagePlanProperties!.weatherRouting.required = isCrossingWeatherRoutingLines(route) || hasBeaufort8Weather(route);
    const weatherRouting = route.passagePlan?.weatherRouting;
    if (!weatherRouting!.required) return;

    let initialWarning = false;

    if (!weatherRouting!.acknowledged) {
        initialWarning = true;
    }

    if (!initialWarning) return;

    const popup = VoyageDialogPopupManager.createDialog({
        headerText: `Request WNI advice`,
        bodyText:   `The route intended; requires WNI advice.
                    As per GLSM ID825 you are to reach out for 
                    WNI advice.`,
        noCloseButton: true,
        acceptButtonText: "Acknowledge"
    });

    await popup.getResponse();

    // update and set plan with user action
    route.passagePlan.weatherRouting!.acknowledged = true;
    VesselPlanningModel!.setPlan(scheduleId, plan);
}

/**
 * @name handleWeatherRoutingPilotPoint
 * @description Show popup warning if current time is after departure pilot point
 * weather is Beaufort 8 or route is crossing weather routing lines.
 */
export async function handleWeatherRoutingPilotPoint(inputPlan: VoyagePlan) {
    let plan = inputPlan;
    const scheduleId = plan.scheduleId;

    // Don't handle warning on both executing and planning plan.
    // Only use executing plan if there is one.
    const executingPlan = await VesselPlanningModel!.getExecutingPlan();
    if (executingPlan && executingPlan.scheduleId === scheduleId) {
        plan = executingPlan;
    }

    const route = plan.routes[plan.selectedRouteIndex];
    if (!route.passagePlan) {
        return
    };

    const weatherRouting = route.passagePlan?.weatherRouting;
    if (!weatherRouting!.required || !weatherRouting!.acknowledged) { 
        return;
    }

    const now = new Date().getTime();
    let postPilotWarning = false;
    let departurePilotEtd;
    let pilotPoint = route.route_geometry.find(feature => feature.properties.pilotPoint);

    if (plan.departurePilotEtd < 0 && pilotPoint) {
        departurePilotEtd = new Date(pilotPoint.properties.timestamp).getTime();
    } else {
        departurePilotEtd = plan.departurePilotEtd;
    }

    if (!weatherRouting!.pilotPointAcknowledged && departurePilotEtd <= now) {
        postPilotWarning = true;
    }

    if (!postPilotWarning) return;

    const popup = VoyageDialogPopupManager.createDialog({
        headerText: `Request WNI advice`,
        bodyText:   `The route intended; requires WNI advice.
                    As per GLSM ID825 you are to reach out for 
                    WNI advice.`,
        noCloseButton: true,
        acceptButtonText: "Acknowledge"
    });

    await popup.getResponse();

    // update and set plan with user action
    route.passagePlan.weatherRouting!.acknowledged = true;
    if (postPilotWarning) {
        route.passagePlan.weatherRouting!.pilotPointAcknowledged = true;
    }

    VesselPlanningModel!.setPlan(scheduleId, plan);
}

/**
 * @name isActiveOrNextScheduleEntry
 * @description Check if scheduleId is active or next schedule entry.
 * Weather Routing should only be checked fo these two schedule entries.
 */
export function isActiveOrNextScheduleEntry(scheduleId: string, scheduleEntries: ScheduleEntry[]) {
    let activeEntryFound = false;

    for (const scheduleEntry of scheduleEntries) {
        if (activeEntryFound) {
            // next entry
            if (scheduleEntry.id === scheduleId) {
                return true;
            }
        }

        if (scheduleEntry.active) {
            activeEntryFound = true;
            if (scheduleEntry.id === scheduleId) {
                return true;
            }
        }
    }

    return false;
}

export function isCrossingWeatherRoutingLines(route: VoyageRoute): boolean {
    const borderLines: TurfFeature<turfLineString>[] = [];
    const features: Feature<Geometry>[] = [];

    //Atlantic Ocean would be a line From 63.00’00N ; 027.00’00W to 60.00’00S ; 027.00’00W
    borderLines.push(turfLine([[-27, 60], [-27, -60]]));
    //Pacific Ocean would be a line From 63.00’00N ; 175.00’00W to 60.00’00S ; 175.00’00W
    borderLines.push(turfLine([[-175, 63], [-175, -60]]));
    //Bay Of Biscay would be a line from 45.30’00N ; 001.10’00 W to 45.30’00N ; 12.00’00 W
    borderLines.push(turfLine([[-1.1666666666666667, 45.5], [-12, 45.5]]));
    //Saracossa Sea would be a line from 17.30’00N ; 61.40’00W to 17.30’00N ; 58.00’00 W
    borderLines.push(turfLine([[-61.666666666666664, 17.5], [-58, 17.5]]));
    //Africa West Coast would be a line from 10.00’00S ; 003.00’00E to 10.00’00S ; 10.00’00 W
    borderLines.push(turfLine([[3, -10], [-10, -10]]));
    //New Zealand to Far East would be 29.30’00S ; 166.00’00E to 29.30’00S ; 175.00’00E
    borderLines.push(turfLine([[166, -29.5], [175, 29.5]]));

    route.route_geometry.filter(f => f.geometry.type === "LineString").forEach(f => {
        const lineString = new Feature({
            geometry: new LineString(f.geometry.coordinates)
        });
        
        if (f.properties.leg_type) {
            lineString.setProperties({"leg_type": f.properties.leg_type});
        }

        features.push(...CreateDateLineAndGreatCircleFeatures(lineString));
    })

    const routeLines = features.filter(f => f.getGeometry()?.getType() === "LineString").map(l => turfLine((<LineString>l.getGeometry()).getCoordinates()));

    let isCrossing = false;

    borderLines!.forEach(borderLine => {
        routeLines.forEach(line => {
            const intersections = lineIntersect(line, borderLine);
            if (intersections.features.length > 0) {
                isCrossing = true;
                return;
            }
        });
        if (isCrossing) return;
    });

    return isCrossing;
}

export function hasBeaufort8Weather(route: VoyageRoute): boolean {
    let hasB8Weather = false;
    route.route_geometry.filter(f => f.geometry.type === "LineString").forEach(f => {
        if (f.properties.weather && f.properties.weather.Wind10M.max.value > 17) {
            hasB8Weather = true;
            return;
        }
        if (hasB8Weather) return;
    });
    
    return hasB8Weather;
}
